import Menu from './menu';

import Util from '../utils/util';

import { Modules } from '@kaetram/common/network';

import type Inventory from './inventory';
import type { Enchantments } from '@kaetram/common/types/item';

const ENCHANTMENT_NAMES_ES: Record<number, string> = {
    [Modules.Enchantment.Bloodsucking]: 'Vampírico',
    [Modules.Enchantment.Critical]: 'Crítico',
    [Modules.Enchantment.Evasion]: 'Evasión',
    [Modules.Enchantment.Thorns]: 'Espinas',
    [Modules.Enchantment.Explosive]: 'Explosivo',
    [Modules.Enchantment.Stun]: 'Aturdir',
    [Modules.Enchantment.AntiStun]: 'Anti-Aturdir',
    [Modules.Enchantment.Splash]: 'Salpicadura',
    [Modules.Enchantment.DoubleEdged]: 'Doble Filo'
};

type SelectCallback = (index: number) => void;
type ConfirmCallback = (index: number, shardIndex: number) => void;

export default class Enchant extends Menu {
    private list: HTMLUListElement = document.querySelector('#enchant-inventory-slots')!;

    private selectedItem: HTMLElement = document.querySelector('#enchant-selected-item')!;
    private selectedShards: HTMLElement = document.querySelector('#enchant-shards')!;
    private selectedShardsCount: HTMLElement = document.querySelector('#shards-count')!;

    private confirmButton: HTMLElement = document.querySelector('#confirm-enchant')!;

    private infoPanel: HTMLElement = document.querySelector('#enchant-info-panel')!;
    private infoName: HTMLElement = document.querySelector('#enchant-info-name')!;
    private infoCurrent: HTMLElement = document.querySelector('#enchant-info-current')!;
    private infoAvailable: HTMLElement = document.querySelector('#enchant-info-available')!;

    private selectedSlot = -1;
    private selectedShard = -1;

    private selectCallback?: SelectCallback;
    private confirmCallback?: ConfirmCallback;

    public constructor(private inventory: Inventory) {
        super('#enchant', '#close-enchant');

        this.selectedItem.addEventListener('click', this.clear.bind(this));
        this.selectedShards.addEventListener('click', this.clear.bind(this));
        this.confirmButton.addEventListener('click', this.confirm.bind(this));

        for (let i = 0; i < Modules.Constants.INVENTORY_SIZE; i++)
            this.list.append(
                Util.createSlot(Modules.ContainerType.Inventory, i, this.select.bind(this))
            );
    }

    public override synchronize(): void {
        if (!this.isVisible()) return;

        this.clearSelections();

        this.inventory.forEachSlot((index: number, slot: HTMLElement) => {
            let image = this.getElement(index).querySelector<HTMLElement>('.item-image')!,
                count = this.getElement(index).querySelector<HTMLElement>('.item-count')!,
                slotImage = slot.querySelector<HTMLElement>('.item-image')!;

            if (!slotImage) return;

            image.style.backgroundImage = slotImage.style.backgroundImage;
            count.textContent = slot.textContent;
        });
    }

    public override show(): void {
        super.show();

        this.synchronize();
    }

    private confirm(): void {
        if (this.selectedSlot === -1 || this.selectedShard === -1) return;

        this.confirmCallback?.(this.selectedSlot, this.selectedShard);
    }

    private select(_type: Modules.ContainerType, index: number): void {
        this.selectCallback?.(index);
    }

    public move(index: number, isShard = false): void {
        let image = this.getElement(index).querySelector<HTMLElement>('.item-image')!,
            count = this.getElement(index).querySelector<HTMLElement>('.item-count')!;

        if (this.selectedSlot !== -1 && !isShard) {
            let element = this.getElement(this.selectedSlot),
                elementImage = element.querySelector<HTMLElement>('.item-image')!;

            elementImage.style.backgroundImage = this.selectedItem.style.backgroundImage;
        }

        if (this.selectedShard !== -1 && isShard) {
            let element = this.getElement(this.selectedShard),
                elementImage = element.querySelector<HTMLElement>('.item-image')!,
                elementCount = element.querySelector<HTMLElement>('.item-count')!;

            elementImage.style.backgroundImage = this.selectedShards.style.backgroundImage;
            elementCount.textContent = this.selectedShardsCount.textContent;
        }

        if (isShard) this.selectedShard = index;
        else this.selectedSlot = index;

        (isShard ? this.selectedShards : this.selectedItem).style.backgroundImage =
            image.style.backgroundImage;

        if (isShard) this.selectedShardsCount.textContent = count.textContent;

        image.style.backgroundImage = '';
        count.textContent = '';
    }

    /**
     * Displays enchantment info for the selected item.
     */

    public showItemInfo(
        itemName?: string,
        enchantments?: Enchantments,
        availableEnchantments?: number[]
    ): void {
        if (!itemName) {
            this.hideItemInfo();
            return;
        }

        this.infoName.textContent = itemName;

        let currentHtml = '';
        let hasEnchants = false;

        if (enchantments) {
            for (let [id, data] of Object.entries(enchantments)) {
                let name = ENCHANTMENT_NAMES_ES[Number(id)] ?? `Enc.${id}`;
                let level = (data as { level: number })?.level ?? 0;

                if (level > 0) {
                    currentHtml += `<div class="enchant-entry">${name} Nv.${level}/15</div>`;
                    hasEnchants = true;
                }
            }
        }

        if (!hasEnchants)
            currentHtml = '<div class="enchant-none">Sin encantamientos</div>';

        this.infoCurrent.innerHTML = currentHtml;

        if (availableEnchantments && availableEnchantments.length > 0) {
            let names = availableEnchantments
                .map((id) => ENCHANTMENT_NAMES_ES[id] ?? `?`)
                .join(', ');
            this.infoAvailable.innerHTML =
                `<span class="avail-label">Posibles:</span><br>` +
                `<span class="avail-list">${names}</span>`;
        } else {
            this.infoAvailable.innerHTML = '';
        }

        this.infoPanel.classList.add('visible');
    }

    private hideItemInfo(): void {
        this.infoPanel.classList.remove('visible');
    }

    private clear(): void {
        this.clearSelections();
        this.synchronize();
    }

    private clearSelections(): void {
        this.selectedSlot = -1;
        this.selectedShard = -1;

        this.selectedItem.style.backgroundImage = '';
        this.selectedShards.style.backgroundImage = '';
        this.selectedShardsCount.textContent = '';

        this.hideItemInfo();
    }

    private getElement(index: number): HTMLElement {
        return this.list.children[index].querySelector('div') as HTMLElement;
    }

    public onSelect(callback: SelectCallback): void {
        this.selectCallback = callback;
    }

    public onConfirm(callback: ConfirmCallback): void {
        this.confirmCallback = callback;
    }
}
